import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import type { NicheConfig } from '../../niches/types';

interface Props {
  config: NicheConfig;
}

export default function NicheWhyMe({ config }: Props) {
  const { whyMe } = config;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="px-6 py-20 md:py-28 max-w-7xl mx-auto w-full">
      <div ref={ref} className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {whyMe.sectionEyebrow && (
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-stroke"></div>
              <span className="text-xs text-muted uppercase tracking-[0.3em]">
                {whyMe.sectionEyebrow}
              </span>
            </div>
          )}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal leading-[1.1] tracking-tight text-text-primary mb-8">
            <span className="font-display italic">{whyMe.sectionTitle}</span>
          </h2>
          <p className="text-base md:text-lg text-muted leading-relaxed whitespace-pre-line">
            {whyMe.body}
          </p>
        </motion.div>

        {whyMe.points && whyMe.points.length > 0 && (
          <div className="space-y-6">
            {whyMe.points.map((point, i) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1, ease: 'easeOut' }}
                className="flex gap-5 border-b border-stroke pb-6 last:border-b-0"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-text-primary text-bg flex items-center justify-center text-sm font-medium">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-semibold text-text-primary mb-1.5">
                    {point.title}
                  </h3>
                  <p className="text-sm text-muted leading-relaxed">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
